import {EditorView, type KeyBinding} from '@codemirror/view'
import {CheckIcon, HeadingIcon} from '@primer/octicons-react'
import {ActionBar} from '@primer/react'
import {KeybindingHint} from '@primer/react/experimental'
import {use, useCallback} from 'react'

import {
  formatHeading1Keybinding,
  formatHeading2Keybinding,
  formatHeading3Keybinding,
  formatHeading4Keybinding,
  formatHeading5Keybinding,
  formatHeading6Keybinding,
} from '../codemirror/extensions/markdown-keymap'
import {SelectionContext} from '../context/SelectionContext'
import {useHybridEditorTelemetry} from '../telemetry'
import styles from './Toolbar.module.css'
import {formatKeybindingHint} from './util'

interface HeadingMenuProps {
  view: EditorView | null
}

const CheckIconPlaceholder = () => <div className={styles.menuLeadingVisualPlaceholder} />

const generateTrailingVisual = ({keys}: {keys: string}) =>
  function TrailingVisual() {
    return <KeybindingHint className={styles.menuKeybindingHint} keys={keys} />
  }

export const HeadingMenu = ({view}: HeadingMenuProps) => {
  const telemetry = useHybridEditorTelemetry()
  const selectionContext = use(SelectionContext)

  const getLeadingVisual = useCallback(
    (level: number) => {
      // Determine if we should show any visual indicators. If no heading is selected, don't show any visual
      const hasHeadingSelection = selectionContext.some(n => n.type === 'heading')
      if (!hasHeadingSelection) {
        return undefined
      }

      // If this level matches the selection, show check icon
      const defaultIsPressed = selectionContext.some(n => n.type === 'heading' && n.depth === level)
      if (defaultIsPressed) {
        return CheckIcon
      }

      // Otherwise, show placeholder to maintain alignment
      return CheckIconPlaceholder
    },
    [selectionContext],
  )

  const getTrailingVisual = useCallback((keybinding: KeyBinding) => {
    const keys = formatKeybindingHint(keybinding.key)
    if (!keys) return undefined
    return generateTrailingVisual({keys})
  }, [])

  const runCommand = useCallback(
    (level: number, keybinding: KeyBinding) => {
      if (!view) return
      const isPressed = selectionContext.some(n => n.type === 'heading' && n.depth === level)
      const commandFn = keybinding.run || (() => false)
      const runWithEventSource = commandFn as (editorView: EditorView, eventSource?: 'keyboard' | 'toolbar') => boolean
      const handled = runWithEventSource(view, 'toolbar')

      telemetry.trackToolbarInteraction({
        control: `heading_${level}`,
        action: `heading_${level}`,
        trigger: 'toolbar',
        isActive: isPressed,
        keybinding: keybinding.key,
        handled,
      })
    },
    [selectionContext, telemetry, view],
  )

  return (
    <ActionBar.Menu
      aria-label="Heading"
      icon={HeadingIcon}
      items={[
        {
          label: 'Heading 1',
          onClick: () => runCommand(1, formatHeading1Keybinding),
          leadingVisual: getLeadingVisual(1),
          trailingVisual: getTrailingVisual(formatHeading1Keybinding),
        },
        {
          label: 'Heading 2',
          onClick: () => runCommand(2, formatHeading2Keybinding),
          leadingVisual: getLeadingVisual(2),
          trailingVisual: getTrailingVisual(formatHeading2Keybinding),
        },
        {
          label: 'Heading 3',
          onClick: () => runCommand(3, formatHeading3Keybinding),
          leadingVisual: getLeadingVisual(3),
          trailingVisual: getTrailingVisual(formatHeading3Keybinding),
        },
        {
          label: 'Heading 4',
          onClick: () => runCommand(4, formatHeading4Keybinding),
          leadingVisual: getLeadingVisual(4),
          trailingVisual: getTrailingVisual(formatHeading4Keybinding),
        },
        {
          label: 'Heading 5',
          onClick: () => runCommand(5, formatHeading5Keybinding),
          leadingVisual: getLeadingVisual(5),
          trailingVisual: getTrailingVisual(formatHeading5Keybinding),
        },
        {
          label: 'Heading 6',
          onClick: () => runCommand(6, formatHeading6Keybinding),
          leadingVisual: getLeadingVisual(6),
          trailingVisual: getTrailingVisual(formatHeading6Keybinding),
        },
        // {type: 'divider'},
        // {
        //   label: 'Increase heading level',
        //   onClick: () => alert('Increase heading level clicked'),
        // },
        // {
        //   label: 'Decrease heading level',
        //   onClick: () => alert('Decrease heading level clicked'),
        // },
      ]}
    />
  )
}

try{ CheckIconPlaceholder.displayName ||= 'CheckIconPlaceholder' } catch {}
try{ TrailingVisual.displayName ||= 'TrailingVisual' } catch {}
try{ HeadingMenu.displayName ||= 'HeadingMenu' } catch {}